import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { fridaveAPI, roundsAPI } from '../../services/api';
import LoadingSpinner from '../../components/common/LoadingSpinner';

export default function FriDaveScoring() {
  const navigate = useNavigate();
  const [round, setRound] = useState(null);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);

  const currentYear = String(new Date().getFullYear());

  useEffect(() => {
    fetchActiveRound();
  }, []);

  const fetchActiveRound = async () => {
    try {
      setLoading(true);
      const rounds = await fridaveAPI.getRounds(currentYear).catch(() => []);
      const list = Array.isArray(rounds) ? rounds : [];
      const active = list.find(r => r.status === 'in_progress' || r.status === 'active');

      if (active) {
        // If a group is already saved for this round, go straight to score entry
        const savedGroup = localStorage.getItem(`round_${active.id}_group`);
        if (savedGroup) {
          navigate(`/fridave/rounds/${active.id}`, { state: { activeTab: 'enter-scores' } });
          return;
        }

        const fullRound = await roundsAPI.getById(active.id);
        setRound(fullRound);

        // Group players by group_number
        const players = fullRound.players || [];
        const grouped = {};
        players.forEach(p => {
          const g = p.group_number || 1;
          if (!grouped[g]) grouped[g] = [];
          grouped[g].push(p);
        });
        setGroups(Object.entries(grouped).sort((a, b) => Number(a[0]) - Number(b[0])));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleGroupSelect = (groupNumber) => {
    if (!round) return;
    localStorage.setItem(`round_${round.id}_group`, groupNumber.toString());
    navigate(`/fridave/rounds/${round.id}`, { state: { activeTab: 'enter-scores' } });
  };

  if (loading) {
    return <div className="flex justify-center py-12"><LoadingSpinner /></div>;
  }

  if (!round) {
    return (
      <div className="px-4 pb-4">
        <div className="bg-white rounded-2xl p-10 text-center shadow-sm border border-gray-200">
          <div className="text-5xl mb-4">📝</div>
          <h2 className="text-xl font-bold text-fd-primary mb-2">No Round in Progress</h2>
          <p className="text-gray-500 text-sm">Score entry is only available during live rounds.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="px-4 pb-4">
      {/* Round info */}
      <div className="bg-white rounded-2xl p-4 mb-4 border border-gray-200 shadow-sm">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs font-bold uppercase tracking-wide text-gray-400 mb-0.5">Round in Progress</p>
            <h2 className="text-lg font-bold text-gray-900">{round.event_name}</h2>
          </div>
          <span className="px-2 py-1 bg-green-100 text-green-700 text-xs font-bold rounded-full animate-pulse">LIVE</span>
        </div>
      </div>

      {/* Group list */}
      <p className="text-sm font-semibold text-gray-500 mb-3 uppercase tracking-wide">Select Your Group</p>
      <div className="space-y-3">
        {groups.map(([groupNum, players]) => (
          <button
            key={groupNum}
            onClick={() => handleGroupSelect(Number(groupNum))}
            className="w-full bg-white rounded-2xl shadow-sm border border-gray-200 p-4 text-left hover:border-fd-primary/40 hover:shadow-md transition-all"
          >
            <p className="text-xs font-bold uppercase tracking-wide text-fd-primary mb-2">Group {groupNum}</p>
            <div className="flex flex-wrap gap-2">
              {players.map(p => (
                <span key={p.player_id} className="text-sm font-semibold text-gray-700 bg-gray-50 px-3 py-1 rounded-full border border-gray-200">
                  {p.player_name}
                </span>
              ))}
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}
